

































































































































































































































import axios from "axios";
import LocalForage from "localforage";
import { assign, Machine } from "xstate";
import { useMachine } from "@xstate/vue";
import { compareAsc, parse } from "date-fns";
import {
  computed,
  defineComponent,
  ref,
  Ref,
  watch
} from "@vue/composition-api";

import store from "@/store/root";
import { Page } from "@/types";
import { auth as manager } from "@/auth";
import { analyseText, ConceptType } from "@/utils/analysis";
import { encodeText } from "@/utils/encoding";

import AuthPanel from "@/components/AuthPanel.vue";
import ModeButton from "@/components/ModeButton.vue";
import ProcessStep from "@/components/steps/ProcessStep.vue";
import DocumentAnalysis from "@/components/msa/DocumentAnalysis.vue";
import TimelineContainer from "@/components/msa/timeline/TimelineContainer.vue";

interface Context {
  step: number;
  jobId: string;
  pages: Page[][];
  documents: Document[];
  document: Document | null;
  file: File | null;
  endpoints: Record<string, string>;
}

const makeFormData = (data: [string, any][]) => {
  const result = new FormData();

  for (let [key, value] of data) {
    result.append(key, value);
  }

  return result;
};

const uploadFile = async (context: Context, event: any) => {
  if (!context.file) {
    return;
  }

  const checksum = Array.from(
    new Uint8Array(
      await crypto.subtle.digest("SHA-256", await context.file.arrayBuffer())
    )
  )
    .map(byte => byte.toString(16).padStart(2, "0"))
    .join("");

  return {
    cached: false,
    id: await axios
      .post("/jobs", makeFormData([["document", context.file]]), {
        baseURL: context.endpoints.textractor,
        headers: await manager.headers()
      })
      .then(response => response.data.id)
  };
};

const processResults = async (context: any, event: any) => {
  const results = await axios
    .get(`/jobs/${context.jobId}/`, {
      headers: await manager.headers(),
      baseURL: context.endpoints.textractor
    })
    .then(response => response.data);

  if (results.outcome !== "success") {
    return results;
  }

  const checksum = Array.from(
    new Uint8Array(
      await crypto.subtle.digest("SHA-256", await context.file.arrayBuffer())
    )
  )
    .map(byte => byte.toString(16).padStart(2, "0"))
    .join("");

  return results;
};

export default defineComponent({
  name: "NewTimelineView",
  components: {
    TimelineContainer,
    ProcessStep,
    AuthPanel,
    ModeButton,
    DocumentAnalysis
  },
  setup: function(props, context) {
    const selected = ref<any>(null);

    const type = ref("1");

    const makeContext = (): Context => ({
      step: 1,
      jobId: "",
      document: null,
      pages: [],
      documents: [],
      file: null,
      endpoints: store.state.settings.current.endpoints
    });

    const extractionMachine = Machine<Context>({
      id: "extraction",
      initial: "idle",
      context: makeContext(),
      states: {
        idle: {
          entry: assign((context, event) => makeContext()),
          on: {
            SELECT: "selected"
          }
        },
        selected: {
          entry: assign({ file: (context, event) => event.file }),
          on: {
            UPLOAD: [
              {
                invoke: {},
                target: "uploading",
                cond: () => {}
              },
              { target: "uploading" }
            ],
            SELECT: "selected"
          }
        },
        uploading: {
          entry: assign({ step: (context, event) => 2 }),
          invoke: {
            src: uploadFile,
            onDone: [
              {
                target: "analysing",
                cond: (context, event) => event.data.cached,
                actions: assign({
                  pages: (context, event) => event.data.results.documents
                })
              },
              {
                target: "processing",
                actions: assign({
                  jobId: (context, event) => event.data.id
                })
              }
            ],
            onError: {
              target: "failure"
            }
          }
        },
        processing: {
          entry: assign({ step: (context, event) => 3 }),
          invoke: {
            src: processResults,
            onDone: [
              {
                target: "analysing",
                cond: (context, event) => event.data.outcome === "success",
                actions: assign({
                  pages: (context, event) => event.data.documents
                })
              },
              {
                target: "pending",
                cond: (context, event) =>
                  event.data.outcome === "error" &&
                  event.data.type === "invalid_status" &&
                  event.data.status === "IN_PROGRESS"
              }
            ],
            onError: {
              target: "failure"
            }
          }
        },
        pending: {
          after: {
            15000: "processing"
          }
        },
        analysing: {
          entry: assign((context, event) => ({ step: 4 })),
          invoke: {
            src: async (context, _) => {
              const headers = await manager.headers();

              return await Promise.all(
                context.pages.map(pages =>
                  Promise.all([
                    analyseText(
                      pages.map(page => page.text).join("\n"),
                      context.endpoints.analysis,
                      headers
                    ),
                    encodeText(
                      pages.map(page => page.text).join("\n"),
                      context.endpoints.snomed,
                      headers
                    )
                  ]).then(([analysis, encoding]) => ({
                    pages,
                    ...analysis,
                    ...encoding
                  }))
                )
              ).then(results =>
                results
                  .sort((a, b) =>
                    compareAsc(
                      parse(a.meta.date, "y-M-d", new Date()),
                      parse(b.meta.date, "y-M-d", new Date())
                    )
                  )
                  .map((document, index) => ({ ...document, index }))
              );
            },
            onDone: {
              target: "analysed",
              actions: assign({
                documents: (context, event) => event.data,
                document: (context, event) => event.data[0]
              })
            },
            onError: "failure"
          }
        },
        analysed: {
          entry: assign((context, event) => ({ step: 5 })),
          on: {
            CLEAR: "idle",
            ANALYSE: {
              target: "analysed",
              actions: assign({
                document: (context, event) => event.document
              })
            }
          }
        },
        failure: {
          on: {
            RETRY: "idle"
          }
        }
      }
    });

    const { state, send } = useMachine(extractionMachine);

    const allMatches = computed(() =>
      [...Object.values(state.value.context.document.matches)]
        .flat(1)
        .filter(match =>
          [
            ConceptType.Disease,
            ConceptType.Drug,
            ConceptType.Procedure,
            ConceptType.DiagProcedure,
            ConceptType.LabProcedure,
            ConceptType.Device
          ].includes(match.concept.concepttype)
        )
    );

    watch(
      () => selected.value,
      match => {
        type.value = match ? match.type : type.value || "1";
      }
    );

    return {
      state,
      send,
      allMatches,
      selected,
      type,
      confirm: (arg: any) => window.confirm(arg)
    };
  }
});
